/**
 * frontend/src/features/telephony/services/useExotelSDK.js
 * ----------------------------------------------------------
 * React hook that manages the full Exotel WebRTC SDK lifecycle.
 *
 * What it does:
 *   1. Dynamically loads the Exotel WebSDK JS file from their CDN
 *   2. When SIP device toggle is turned ON — fetches WebRTC token from our backend
 *      and registers the browser as a SIP softphone with Exotel
 *   3. Listens for incoming call events → triggers onIncomingCall() callback
 *   4. Exposes: makeCall(), answerCall(), declineCall(), endCall(), toggleMute(), toggleHold()
 *   5. When SIP device toggle is turned OFF — unregisters the SDK session
 *
 * Usage:
 *   const sdk = useExotelSDK({ onIncomingCall, onCallConnected, onCallEnded })
 *   sdk.initialize()    // called when SIP toggle is turned ON
 *   sdk.destroy()       // called when SIP toggle is turned OFF
 *   sdk.makeCall('+91...')
 */

import { useRef, useCallback, useEffect, useMemo } from 'react'
import ExotelCRMWebSDK from '@exotel-npm-dev/exotel-ip-calling-crm-websdk'
import { fetchAgentSession } from './telephonyApi'
import {
  runWithScopedFetchInterceptor,
  forceRestoreFetchInterceptor,
  calculateTokenRenewDelay,
  isTokenExpiredError,
} from './telephonyHelpers'

/**
 * @param {object} callbacks
 * @param {function} callbacks.onIncomingCall   - (callInfo) => void
 * @param {function} callbacks.onCallConnected  - (callInfo) => void
 * @param {function} callbacks.onCallEnded      - (callInfo) => void
 * @param {function} callbacks.onRegistered     - () => void
 * @param {function} callbacks.onRegistrationFailed - (err) => void
 * @param {function} callbacks.onError          - (err) => void
 */
export function useExotelSDK({
  onIncomingCall = () => {},
  onCallConnected = () => {},
  onCallEnded = () => {},
  onRegistered = () => {},
  onRegistrationFailed = () => {},
  onError = () => {},
} = {}) {
  const sdkInstanceRef = useRef(null)
  const activeCallRef = useRef(null)
  const appIdRef = useRef(null)
  const isOutboundCallRef = useRef(false)
  const outboundTargetRef = useRef(null)
  const isInitializingRef = useRef(false)
  const isRegisteredRef = useRef(false)

  // Token auto-renewal and recovery refs
  const tokenExpiresAtRef = useRef(null)
  const tokenRefreshTimerRef = useRef(null)
  const isRefreshingRef = useRef(false)
  const renewalPendingRef = useRef(false)
  const refreshTokenAndReRegisterRef = useRef(null)

  // Keep fresh callback references across renders without re-initializing SDK
  const callbacksRef = useRef({
    onIncomingCall,
    onCallConnected,
    onCallEnded,
    onRegistered,
    onRegistrationFailed,
    onError,
  })
  useEffect(() => {
    callbacksRef.current = {
      onIncomingCall,
      onCallConnected,
      onCallEnded,
      onRegistered,
      onRegistrationFailed,
      onError,
    }
  })

  /**
   * Unlock HTML5 AudioContext and verify microphone permission
   * to ensure WebRTC SDP handshake never rejects incoming SIP INVITE.
   */
  const unlockAudioContext = async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (AudioContextClass) {
        const ctx = new AudioContextClass()
        if (ctx.state === 'suspended') {
          await ctx.resume()
        }
        const buffer = ctx.createBuffer(1, 1, 22050)
        const source = ctx.createBufferSource()
        source.buffer = buffer
        source.connect(ctx.destination)
        source.start(0)
      }
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          stream.getTracks().forEach((track) => track.stop())
          console.log('[ExotelSDK] Microphone permission confirmed')
        } catch (micErr) {
          console.warn('[ExotelSDK] Microphone permission warning:', micErr)
        }
      }
    } catch (e) {
      console.warn('[ExotelSDK] AudioContext unlock warning:', e)
    }
  }

  const clearRenewalTimer = useCallback(() => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current)
      tokenRefreshTimerRef.current = null
    }
  }, [])

  const scheduleTokenRenewal = useCallback((expiresAt, ttlSeconds) => {
    clearRenewalTimer()
    if (!isRegisteredRef.current && !isInitializingRef.current) {
      return
    }
    const delay = calculateTokenRenewDelay(expiresAt, ttlSeconds)
    console.log(
      `[ExotelSDK] Scheduling token renewal in ${Math.round(delay / 1000)}s (target expiry: ${expiresAt || 'unknown'})`
    )
    tokenRefreshTimerRef.current = setTimeout(() => {
      // Check if call is currently active
      if (activeCallRef.current) {
        console.log('[ExotelSDK] Active call in progress. Deferring token renewal until call finishes.')
        renewalPendingRef.current = true
      } else {
        refreshTokenAndReRegisterRef.current?.(true)
      }
    }, delay)
  }, [clearRenewalTimer])

  // Call listener callbacks matching Exotel CRM WebSDK interface
  const handleCallEvents = useCallback((event, callData) => {
    console.log(`[ExotelSDK] Call event: ${event}`, callData)
    if (event === 'incoming') {
      if (isOutboundCallRef.current) {
        // In Exotel 2-leg calling, Exotel dials the agent's browser first (Leg 1).
        // Auto-accept Leg 1 so browser audio opens and Exotel immediately dials the customer (Leg 2).
        console.log('[ExotelSDK] Outbound call Leg 1 received. Auto-accepting Leg 1...', callData)
        activeCallRef.current = callData
        try {
          if (sdkInstanceRef.current?.AcceptCall) {
            sdkInstanceRef.current.AcceptCall()
            console.log('[ExotelSDK] Leg 1 auto-accepted successfully')
          }
        } catch (acceptErr) {
          console.error('[ExotelSDK] Error auto-accepting Leg 1:', acceptErr)
        }
      } else {
        // Standard customer inbound call
        const popupInfo = {
          callSid: callData?.callId || callData?.sessionId || callData?.callSid || 'test_sid',
          from: callData?.remoteId || callData?.callFromNumber || '',
          direction: 'inbound',
          rawCallData: callData,
        }
        activeCallRef.current = callData
        callbacksRef.current.onIncomingCall(popupInfo)
      }
    } else if (event === 'connected') {
      console.log('[ExotelSDK] Call connected successfully', callData)
      activeCallRef.current = callData
      callbacksRef.current.onCallConnected(callData)
    } else if (event === 'callEnded') {
      console.log('[ExotelSDK] Call ended event received', callData)
      const wasOutbound = isOutboundCallRef.current
      activeCallRef.current = null
      isOutboundCallRef.current = false
      outboundTargetRef.current = null
      callbacksRef.current.onCallEnded({ wasOutbound, callData })

      // If token renewal was deferred while on a call, execute it immediately now!
      if (renewalPendingRef.current) {
        console.log('[ExotelSDK] Deferred token renewal triggering now that call has ended.')
        renewalPendingRef.current = false
        refreshTokenAndReRegisterRef.current?.(true)
      }
    }
  }, [])

  const handleRegisterEvents = useCallback((state) => {
    console.log('[ExotelSDK] Registration event:', state)
    if (state === 'registered' || state === 'Registered' || state === 'registration_success') {
      isRegisteredRef.current = true
      isInitializingRef.current = false
      callbacksRef.current.onRegistered()
    } else if (state === 'registrationFailed' || state === 'RegistrationFailed' || state === 'failed') {
      // If we are currently refreshing the token in background, avoid triggering onRegistrationFailed from old instance
      if (!isRefreshingRef.current) {
        isRegisteredRef.current = false
        isInitializingRef.current = false
        callbacksRef.current.onRegistrationFailed(new Error(state))
      }
    }
  }, [])

  const handleSessionEvents = useCallback((state, info) => {
    console.log('[ExotelSDK] Session event:', state, info)
  }, [])

  /**
   * Refreshes the WebRTC session token from the backend and re-registers the softphone.
   * Guaranteed safe: if a call is active, it defers until the call ends.
   */
  const refreshTokenAndReRegister = useCallback(async (forceRefresh = true) => {
    if (isRefreshingRef.current) {
      console.log('[ExotelSDK] Token refresh already in flight. Skipping.')
      return
    }
    if (!isRegisteredRef.current && !isInitializingRef.current) {
      console.log('[ExotelSDK] Softphone is not registered or active. Skipping token renewal.')
      return
    }

    isRefreshingRef.current = true
    console.log(`[ExotelSDK] Refreshing softphone session token (forceRefresh=${forceRefresh})...`)

    try {
      const sessionData = await fetchAgentSession(forceRefresh)
      if (!sessionData?.success || !sessionData?.data?.token) {
        throw new Error(sessionData?.detail || 'Failed to refresh agent session token')
      }

      const { token, app_user_id, app_id, expires_at, ttl } = sessionData.data
      appIdRef.current = app_id
      tokenExpiresAtRef.current = expires_at

      // Schedule next proactive renewal
      scheduleTokenRenewal(expires_at, ttl)

      // If call started while fetching token, defer device re-registration until call ends
      if (activeCallRef.current) {
        console.log('[ExotelSDK] Call became active during token fetch; deferring device re-registration until call ends.')
        renewalPendingRef.current = true
        isRefreshingRef.current = false
        return
      }

      // Re-register softphone with the fresh token
      console.log('[ExotelSDK] Re-registering softphone with fresh token...')
      const oldSdk = sdkInstanceRef.current
      if (oldSdk) {
        try {
          oldSdk.UnRegisterDevice()
        } catch (e) {
          console.warn('[ExotelSDK] Warning unregistering previous SDK instance during renewal:', e)
        }
      }

      const newSdk = new ExotelCRMWebSDK(token, app_user_id, true)
      const webPhone = await newSdk.Initialize(
        handleCallEvents,
        handleRegisterEvents,
        handleSessionEvents
      )

      if (webPhone) {
        sdkInstanceRef.current = webPhone
        console.log('[ExotelSDK] Softphone session token renewed and device re-registered successfully')
      }
    } catch (err) {
      console.error('[ExotelSDK] Token renewal failed:', err)
      // Retry in 30 seconds if device is still supposed to be registered
      if (isRegisteredRef.current) {
        clearRenewalTimer()
        tokenRefreshTimerRef.current = setTimeout(() => {
          refreshTokenAndReRegisterRef.current?.(true)
        }, 30_000)
      }
    } finally {
      isRefreshingRef.current = false
    }
  }, [clearRenewalTimer, handleCallEvents, handleRegisterEvents, handleSessionEvents, scheduleTokenRenewal])

  // Keep ref up to date
  useEffect(() => {
    refreshTokenAndReRegisterRef.current = refreshTokenAndReRegister
  }, [refreshTokenAndReRegister])

  /**
   * Initialize the Exotel WebRTC SDK.
   * Called when the SIP Device Toggle switch is turned ON.
   * 1. Fetches WebRTC token from our FastAPI backend (GET /api/telephony/agent-session)
   * 2. Initializes the SDK with the token and registers the browser as a SIP device
   */
  const initialize = useCallback(async () => {
    // Prevent duplicate registrations or concurrent REGISTER requests
    if (sdkInstanceRef.current && isRegisteredRef.current) {
      console.log('[ExotelSDK] Softphone already initialized and registered. Skipping duplicate initialize.')
      return
    }
    if (isInitializingRef.current) {
      console.log('[ExotelSDK] Initialization already in progress. Skipping duplicate initialize.')
      return
    }

    isInitializingRef.current = true

    try {
      unlockAudioContext().catch(() => {})

      // Step 1: Fetch WebRTC token from our backend
      const sessionData = await fetchAgentSession()
      if (!sessionData?.success) {
        throw new Error(sessionData?.detail || 'Failed to fetch WebRTC session token')
      }

      const { token, app_user_id, app_id, expires_at, ttl } = sessionData.data
      appIdRef.current = app_id
      tokenExpiresAtRef.current = expires_at

      // Schedule proactive token renewal
      scheduleTokenRenewal(expires_at, ttl)

      // Step 2: Instantiate ExotelCRMWebSDK with parameters: accessToken, agentUserID, autoConnectVOIP
      const sdk = new ExotelCRMWebSDK(token, app_user_id, true)

      // Step 3: Initialize the SDK which returns the webPhone object (ExotelWebPhoneSDK)
      const webPhone = await sdk.Initialize(
        handleCallEvents,
        handleRegisterEvents,
        handleSessionEvents
      )

      if (!webPhone) {
        throw new Error('WebRTC softphone initialization returned empty configuration')
      }

      sdkInstanceRef.current = webPhone
      console.log('[ExotelSDK] WebRTC softphone initialized and registered successfully')
    } catch (err) {
      console.error('[ExotelSDK] Initialization failed:', err)
      isInitializingRef.current = false
      isRegisteredRef.current = false
      clearRenewalTimer()
      callbacksRef.current.onError(err)
    }
  }, [clearRenewalTimer, handleCallEvents, handleRegisterEvents, handleSessionEvents, scheduleTokenRenewal])

  /**
   * Unregister and destroy the Exotel SDK session.
   * Called when the SIP Device Toggle switch is turned OFF.
   */
  const destroy = useCallback(() => {
    clearRenewalTimer()
    forceRestoreFetchInterceptor()
    isInitializingRef.current = false
    isRegisteredRef.current = false
    isOutboundCallRef.current = false
    outboundTargetRef.current = null
    tokenExpiresAtRef.current = null
    renewalPendingRef.current = false
    isRefreshingRef.current = false

    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.HangupCall()
      } catch (e) {}
      try {
        sdkInstanceRef.current.UnRegisterDevice()
      } catch (e) {
        console.warn('[ExotelSDK] Error during unregister:', e)
      }
      sdkInstanceRef.current = null
      activeCallRef.current = null
      console.log('[ExotelSDK] SDK unregistered and destroyed')
    }
  }, [clearRenewalTimer])

  /**
   * Tab sleep / wake-up listener:
   * When computer wakes up from sleep or user returns to the tab, check if token
   * has expired or is expiring soon (within 5 minutes). If so, trigger an immediate refresh.
   */
  useEffect(() => {
    const handleWakeup = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      // Only proceed if softphone is registered and not currently in an active call
      if (!isRegisteredRef.current || activeCallRef.current || isRefreshingRef.current) {
        return
      }

      const expiresAt = tokenExpiresAtRef.current
      if (!expiresAt) return

      const expTime = new Date(expiresAt).getTime()
      if (Number.isNaN(expTime)) return

      const msLeft = expTime - Date.now()
      // If token expired or expiring in <= 5 minutes (300,000 ms), refresh immediately
      if (msLeft <= 300_000) {
        console.log(
          `[ExotelSDK] Tab wake-up detected with near-expired/expired token (${Math.round(msLeft / 1000)}s left). Triggering urgent refresh.`
        )
        refreshTokenAndReRegister(true)
      }
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleWakeup)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleWakeup)
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleWakeup)
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleWakeup)
      }
    }
  }, [refreshTokenAndReRegister])

  // Cleanup timers on component unmount
  useEffect(() => {
    return () => {
      clearRenewalTimer()
      forceRestoreFetchInterceptor()
    }
  }, [clearRenewalTimer])

  /**
   * Initiate an outbound call.
   * @param {string} toNumber - The customer phone number to dial
   */
  const makeCall = useCallback((toNumber) => {
    if (!sdkInstanceRef.current) {
      console.warn('[ExotelSDK] SDK not initialized. Turn on SIP Device first.')
      return false
    }
    try {
      let cleanTarget = String(toNumber || '').trim().replace(/[\s\-\(\)]/g, '')
      console.log(`[ExotelSDK] Initiating MakeCall to target: ${cleanTarget}`)

      // Mark this as an outbound call before dialing so incoming Leg 1 is auto-accepted
      isOutboundCallRef.current = true
      outboundTargetRef.current = cleanTarget

      runWithScopedFetchInterceptor({
        urlPattern: '/v2/integrations/call/outbound_call',
        transformRequest: (input, options) => {
          if (options?.body && typeof options.body === 'string' && appIdRef.current) {
            try {
              const body = JSON.parse(options.body)
              if (!body.app_id) {
                body.app_id = appIdRef.current
                options.body = JSON.stringify(body)
              }
            } catch (e) {
              console.warn('[ExotelSDK] Could not parse outbound_call body:', e)
            }
          }
          return { url: input, options }
        },
        action: () => {
          sdkInstanceRef.current.MakeCall(cleanTarget, (status, data) => {
            console.log(`[ExotelSDK] MakeCall status: ${status}`, data)
            if (status === 'success') {
              console.log('[ExotelSDK] Outbound call successfully initiated with CallSid:', data?.Data?.CallSid)
            } else {
              isOutboundCallRef.current = false
              outboundTargetRef.current = null
              let errorMsg = 'Outbound calling failed.'
              if (data?.Error) {
                try {
                  const parsed = typeof data.Error === 'string' ? JSON.parse(data.Error) : data.Error
                  if (parsed?.response?.error_data?.description) {
                    errorMsg = parsed.response.error_data.description
                  } else if (parsed?.response?.error_data?.message) {
                    errorMsg = parsed.response.error_data.message
                  }
                } catch (e) {
                  errorMsg = typeof data.Error === 'string' ? data.Error : JSON.stringify(data.Error)
                }
              }

              // Reactive recovery: if error indicates token expiration or unauthorized, refresh token
              if (isTokenExpiredError(errorMsg) || isTokenExpiredError(data?.Error)) {
                console.warn('[ExotelSDK] MakeCall failed due to token expiration. Triggering background refresh.', errorMsg)
                refreshTokenAndReRegisterRef.current?.(true)
                callbacksRef.current.onError(
                  new Error('Session token expired. Refreshing softphone session, please retry in a few seconds.')
                )
              } else {
                callbacksRef.current.onError(new Error(errorMsg))
              }
            }
          })
        },
        timeoutMs: 5000,
      })

      return true
    } catch (err) {
      console.error('[ExotelSDK] makeCall error:', err)
      isOutboundCallRef.current = false
      outboundTargetRef.current = null
      callbacksRef.current.onError(err)
      return false
    }
  }, [])

  /**
   * Answer an incoming call.
   */
  const answerCall = useCallback(() => {
    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.AcceptCall()
        console.log('[ExotelSDK] Call answered')
      } catch (err) {
        console.error('[ExotelSDK] answerCall error:', err)
      }
    }
  }, [])

  /**
   * Decline / reject an incoming call.
   */
  const declineCall = useCallback(() => {
    isOutboundCallRef.current = false
    outboundTargetRef.current = null
    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.HangupCall()
        console.log('[ExotelSDK] Call declined')
      } catch (err) {
        console.error('[ExotelSDK] declineCall error:', err)
      }
    }
  }, [])

  /**
   * End the active call (hang up).
   */
  const endCall = useCallback(() => {
    isOutboundCallRef.current = false
    outboundTargetRef.current = null
    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.HangupCall()
        console.log('[ExotelSDK] Call ended')
      } catch (err) {
        console.error('[ExotelSDK] endCall error:', err)
      }
    }
  }, [])

  /**
   * Toggle microphone mute/unmute on active call.
   * @param {boolean} shouldMute
   */
  const setMute = useCallback(() => {
    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.ToggleMute()
        console.log('[ExotelSDK] Mute status toggled')
      } catch (err) {
        console.error('[ExotelSDK] ToggleMute error:', err)
      }
    }
  }, [])

  /**
   * Toggle call hold/unhold on active call.
   * @param {boolean} shouldHold
   */
  const setHold = useCallback(() => {
    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.ToggleHold()
        console.log('[ExotelSDK] Hold status toggled')
      } catch (err) {
        console.error('[ExotelSDK] ToggleHold error:', err)
      }
    }
  }, [])

  return useMemo(() => ({
    initialize,
    destroy,
    makeCall,
    answerCall,
    declineCall,
    endCall,
    setMute,
    setHold,
  }), [
    initialize,
    destroy,
    makeCall,
    answerCall,
    declineCall,
    endCall,
    setMute,
    setHold,
  ])
}
